/**
 * Authentication Redirect Utility
 * 
 * Handles redirecting users to login when they receive 401 (Unauthorized) responses
 */

const AUTH_REDIRECT_FLAG_KEY = 'quotehub_auth_redirect_in_progress';
const AUTH_REDIRECT_FLAG_TTL_MS = 10_000;

/**
 * Function to handle 401 redirects - will be set by AppContext
 */
let handleUnauthorized: (() => void) | null = null;

/**
 * Set the unauthorized handler function
 * Called by AppContext to provide access to Auth0 loginWithRedirect
 */
export function setUnauthorizedHandler(handler: () => void): void {
  handleUnauthorized = handler;
}

/**
 * Handle 401 Unauthorized response by redirecting to login
 */
export function handle401Unauthorized(): void {
  // Avoid redirect storms when multiple requests fail at once.
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_FLAG_KEY);
    const last = raw ? Number(raw) : 0;
    if (last && Number.isFinite(last) && Date.now() - last < AUTH_REDIRECT_FLAG_TTL_MS) {
      return;
    }
    sessionStorage.setItem(AUTH_REDIRECT_FLAG_KEY, String(Date.now()));
  } catch {
    // If sessionStorage is unavailable, we still attempt to redirect.
  }

  // Clear any legacy token if present (Auth0 SDK manages its own tokens).
  try {
    localStorage.removeItem('auth_token');
  } catch {
    // ignore
  }

  if (handleUnauthorized) {
    handleUnauthorized();
  } else {
    // Fallback: redirect to Auth0 login if handler not set
    // This should not happen in normal operation, but provides a safety net
    console.warn('Unauthorized handler not set. Redirecting to login page.');
    window.location.href = '/';
  }
}

/**
 * Check if an error is a 401 Unauthorized error
 */
export function is401Error(error: any): boolean {
  if (error?.status === 401 || error?.statusCode === 401) {
    return true;
  }
  
  // Check if it's an ApiError with status 401
  if (error?.name === 'ApiError' && error?.status === 401) {
    return true;
  }
  
  // Check if it's an ApiClientError with statusCode 401
  if (error?.name === 'ApiClientError' && error?.statusCode === 401) {
    return true;
  }
  
  return false;
}

